import { fetcher, formatLongDate, sanitizeFilename } from '@commenda/utils';

export type CompanyChartKind = 'organization-chart' | 'tax-chart';

// Browsers ignore the BE's Content-Disposition on fetch+Blob downloads,
// so the filename is recomputed here and must match what the BE writes.
const CHART_REPORT_TITLES: Record<CompanyChartKind, string> = {
  'organization-chart': 'Organization Chart Report',
  'tax-chart': 'Tax Chart Report',
};

const triggerBlobDownload = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const filenameFor = (
  kind: CompanyChartKind,
  companyName?: string | null,
): string => {
  const safe = sanitizeFilename(companyName, 'Company');
  return `${safe} - ${CHART_REPORT_TITLES[kind]} - ${formatLongDate()}.pdf`;
};

const readError = async (
  response: Response,
  kind: CompanyChartKind,
): Promise<string> => {
  let message = `Failed to export ${CHART_REPORT_TITLES[kind]}`;
  try {
    const body: unknown = await response.json();
    if (body && typeof body === 'object' && 'message' in body && typeof (body as { message: unknown }).message === 'string') {
      message = (body as { message: string }).message;
    }
  } catch {
    /* non-JSON body */
  }
  return message;
};

export const exportCompanyChartReport = async (
  companyId: number,
  kind: CompanyChartKind,
  companyName: string | null | undefined,
  pngDataUri: string,
): Promise<void> => {
  const response = await fetcher(
    `company/${companyId}/reports/charts/${kind}`,
    {
      method: 'POST',
      body: { format: 'pdf', attachment: pngDataUri },
    },
  );
  if (!response.ok) throw new Error(await readError(response, kind));
  const blob = await response.blob();
  triggerBlobDownload(blob, filenameFor(kind, companyName));
};
